import {
  getCachedContentCards,
  logContentCardClick,
  logCardDismissal,
  logContentCardImpressions,
  requestContentCardsRefresh,
} from "@braze/web-sdk";
import {
  IContentCards,
  IContentCardsResult,
} from "@origin-digital/event-dispatcher";

import { IBrazeContentCard } from "../consts/interfaces";
import { readLastRefreshDate, writeLastRefreshDate } from "../utils/storage";

import { logContentCardCustomEvent } from "../utils/events";
import { setCustomUserAttribute } from "../utils/user";
import {
  getFilteredRankedCards,
  translateBrazeCardsToContentCards,
} from "./cardUtils";

// Get all currently cached cards from the last content cards refresh.
export const getContentCards = (
  limit: number | undefined,
  location: string | undefined
): IContentCardsResult => {
  try {
    const brazeContentCards = getCachedContentCards();
    // lastUpdated: if null, it means the content cards are still being fetched for this user.
    if (!brazeContentCards?.cards.length || !brazeContentCards.lastUpdated) {
      return { cards: [] };
    }
    // 1. convert Braze cards to ContentCards
    const standardisedContentCards = translateBrazeCardsToContentCards(
      brazeContentCards.cards as IBrazeContentCard[],
      logContentCardClick,
      logCardDismissal,
      logContentCardImpressions,
      logContentCardCustomEvent,
      setCustomUserAttribute
    );

    // 2. filter + rank cards
    const filteredCards = getFilteredRankedCards(
      standardisedContentCards,
      location,
      limit
    );

    if (!filteredCards.length) {
      return { cards: [] };
    }

    return { cards: filteredCards };
  } catch {
    return { cards: [] };
  }
};

export const REFRESH_TIMEOUT_10s = 10 * 1000;

let inFlightRefresh: Promise<boolean> | null = null;

// Requests an immediate refresh of content cards from Braze servers.
// By default, content cards are refreshed when a new session opens.
// If you want to refresh content cards from the server at another time you must call this function.
export const refreshContentCards = (): Promise<boolean> => {
  // Placements fetch independently and all see a stale cache until the first
  // refresh resolves, so share one SDK request rather than firing one each.
  if (inFlightRefresh) return inFlightRefresh;

  let timer: ReturnType<typeof setTimeout> | undefined;

  const request = new Promise<boolean>((resolve) => {
    requestContentCardsRefresh(
      () => {
        writeLastRefreshDate();
        resolve(true);
      },
      // on failure
      () => resolve(false)
    );
  });

  // The SDK returns without invoking either callback when it is disabled or
  // still waiting on a session. Without this the guard would never clear, and
  // every later fetch would wait out its own 30s timeout before falling back.
  const bailOut = new Promise<boolean>((resolve) => {
    timer = setTimeout(() => resolve(false), REFRESH_TIMEOUT_10s);
  });

  inFlightRefresh = Promise.race([request, bailOut]).finally(() => {
    clearTimeout(timer);
    inFlightRefresh = null;
  });

  return inFlightRefresh;
};
const TIME_DURATION_2s = 2 * 1000;
const TIME_DURATION_20s = 20 * 1000;

export const isContentCardsStale = (
  ttl: number = TIME_DURATION_20s
): boolean => {
  // getCachedContentCards returns undefined before the first refresh completes
  if (getCachedContentCards()?.lastUpdated == null) return true;

  const lastRefresh = readLastRefreshDate();
  if (lastRefresh == null) return true;

  return lastRefresh.getTime() + ttl < Date.now();
};

function delayFn(t: number) {
  return new Promise((resolve) => setTimeout(resolve, t));
}

const doFetchContentCards = async (
  payload: IContentCards["payload"],
  hasRetried = false
): Promise<IContentCardsResult> => {
  const { forceRefresh, limit = 1, location } = payload;
  const isRefresh =
    isContentCardsStale() ||
    (forceRefresh && isContentCardsStale(TIME_DURATION_2s));
  if (isRefresh) {
    // if the content cards are stale, refresh them
    await refreshContentCards();
  }
  const result = getContentCards(limit, location);
  // every caller gets a single retry when the cache came back short
  const isRetry = !hasRetried && result.cards.length < limit;
  if (isRetry) {
    await delayFn(TIME_DURATION_2s);
    return doFetchContentCards({ ...payload, forceRefresh: true }, true);
  }
  return result;
};

export const fetchContentCards = async (
  payload: IContentCards["payload"],
  useCached?: boolean
): Promise<IContentCardsResult> => {
  if (useCached) {
    return getContentCards(payload.limit, payload.location);
  }
  return doFetchContentCards(payload);
};

export const backgroundRefresh = async () => {
  const TTL_60s = 1000 * 60;

  if (isContentCardsStale(TTL_60s)) {
    refreshContentCards();
  }
};
